import React, { Component } from 'react';
import { makeStyles } from 'tss-react/mui';
import Typography from '@mui/material/Typography';
import { Widget, Section } from 'components/interface/Widget/Widget';
import PageInfoText from 'components/interface/InfoText/desktop/InfoText';
import NotFound from 'components/interface/NotFound/NotFound';
import File from 'components/interface/files/File';
import FileDropZone from 'components/interface/files/desktop/FileDropZone';
import BlockRoute from 'components/interface/BlockRoute/BlockRoute';
import ReactGA from 'react-ga4';
import PDFIcon from '@mui/icons-material/PictureAsPdfOutlined';
import { IconButton } from 'components/IconButton';
import { Button } from 'components/Button';
import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';

const useStyles = makeStyles()((theme, params, classes) => ({
    description: {
        width: '100%',
        padding: theme.spacing(2, 2, 1, 2),
        userSelect: 'none',
    },
    filesWrapper: {
        width: '100%',
        maxHeight: '350px',
        minHeight: '232px',
        overflow: 'auto',
        padding: theme.spacing(0, 2),
        paddingBottom: theme.spacing(4),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: theme.spacing(),
    },
    dropZone: {
        width: '100%',
        padding: theme.spacing(0, 2, 2, 2),
    },
    notFound: {
        maxWidth: '348px',
    },
    bookingConfirmation: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: theme.spacing(),
        paddingRight: theme.spacing(),
        flexShrink: 0,
    },
    bookingConfirmationRow: {
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '58px',
    },
    fileStart: {
        width: '1%',
        flexGrow: '1',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
    },
    icon: {
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(),
        marginRight: theme.spacing(),
    },
    name: {
        'marginLeft': theme.spacing(),
        'display': 'flex',
        'flexDirection': 'column',
        'alignItems': 'flex-start',
        'justifyContent': 'center',
        'maxWidth': '100%',
        'minWidth': 0,
        '& > p': {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            maxWidth: '100%',
        },
    },
    dateText: {
        lineHeight: '1',
    },
    fileEnd: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        flexShrink: '0',
    },
    signature: {
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        paddingBottom: theme.spacing(2),
    },
}));

const parseDate = date => (date instanceof Date ? date : parseISO(date));

function BookingConfirmationRow({
    bookingConfirmation,
    onDownload,
    onSignBookingConfirmation,
}) {
    const { classes } = useStyles();
    if (!bookingConfirmation?.date) {
        return null;
    }

    const createdDate = parseDate(bookingConfirmation.date);
    const needsSignature =
        bookingConfirmation?.requiresSignature === true &&
        bookingConfirmation?.document?.signedBySupplier === false;

    return (
        <div className={classes.bookingConfirmation}>
            <div className={classes.bookingConfirmationRow}>
                <div className={classes.fileStart}>
                    <PDFIcon className={classes.icon} />
                    <div className={classes.name}>
                        <Typography variant='body1' color='textPrimary'>
                            Bokningsbekräftelse.pdf
                        </Typography>
                        <Typography
                            variant='caption'
                            color='textSecondary'
                            className={classes.dateText}>
                            Skapad {format(createdDate, 'P', { locale: sv })}
                        </Typography>
                    </div>
                </div>
                <div className={classes.fileEnd}>
                    <IconButton
                        variant='text'
                        icon='download'
                        sx={{ mr: 1 }}
                        onClick={onDownload}
                        size='sm'
                    />
                </div>
            </div>
            {needsSignature ? (
                <div className={classes.signature}>
                    <Button
                        variant='outlined'
                        onClick={onSignBookingConfirmation}
                        fullWidth>
                        Signera dokument
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

export default function FilesWidget({
    files,
    queue,
    onDownload,
    onTrash,
    onUpload,
    onEnqueue,
    onDequeue,
    onRenameQueuedFile,
    loading,
    uploading,
    error,
    onSignFile,
    bookingConfirmation,
    onDownloadBookingConfirmation,
    onSignBookingConfirmation,
}) {
    const { classes, cx } = useStyles();
    const hasBookingConfirmation = !!bookingConfirmation?.date;

    if (loading) {
        files = Array(4).fill({
            id: 0,
            name: '',
            uploader: {},
            category: {},
            size: 0,
        });
    }

    return (
        <Widget title='Dokument'>
            <Typography
                variant='body2'
                color='textPrimary'
                className={classes.description}>
                Här kan du ladda upp/ned dokument kopplat till ditt uppdrag.
            </Typography>
            <div className={classes.filesWrapper}>
                <BookingConfirmationRow
                    bookingConfirmation={bookingConfirmation}
                    onDownload={onDownloadBookingConfirmation}
                    onSignBookingConfirmation={onSignBookingConfirmation}
                />
                {files
                    ? files.map((file, i) => (
                          <File
                              key={file.id + '' + i}
                              {...file}
                              onDownload={file?.onDownload || onDownload}
                              onTrash={onTrash}
                              isLoading={loading}
                              onSignFile={onSignFile}
                          />
                      ))
                    : undefined}
            </div>
            <div className={classes.dropZone}>
                <FileDropZone
                    queue={queue}
                    onDrop={onEnqueue}
                    onRemove={onDequeue}
                    onRename={onRenameQueuedFile}
                    onUpload={onUpload}
                    uploading={uploading}
                />
            </div>
            <BlockRoute
                shouldBlockNavigation={queue.length > 0}
                confirmFunc={() => {
                    ReactGA.event({
                        category: 'Files',
                        action: 'Save changes prompt',
                        label: 'stay',
                    });
                }}
                cancelFunc={() => {
                    ReactGA.event({
                        category: 'Files',
                        action: 'Save changes prompt',
                        label: 'leave',
                    });
                }}
            />
        </Widget>
    );
}
